import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Layers, CheckCircle2, AlertCircle, Sparkles, Search, Loader2, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  real:    { label: 'Live backend',  color: 'emerald', icon: CheckCircle2 },
  ui_only: { label: 'UI-only',       color: 'amber',   icon: AlertCircle },
  stub:    { label: 'Stub',          color: 'rose',    icon: AlertCircle },
  static:  { label: 'Static / marketing', color: 'slate', icon: Sparkles },
};

const COLOR_CLASSES = {
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  amber:   'bg-amber-500/15   text-amber-300   border-amber-400/40',
  rose:    'bg-rose-500/15    text-rose-300    border-rose-400/40',
  slate:   'bg-slate-700/30   text-slate-300   border-slate-500/40',
};

const AdminFeatureCoverage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');  // all | real | ui_only | stub | static
  const [query, setQuery] = useState('');

  const _authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/admin/feature-coverage`, { headers: _authHeaders() });
      setData(r.data);
    } catch (_e) { setData(null); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const visiblePages = useMemo(() => {
    if (!data) return [];
    return data.pages.filter((p) => {
      if (filter !== 'all' && p.status !== filter) return false;
      if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [data, filter, query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-300 mr-2" /> Scanning pages…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white text-slate-900 px-6 py-12">
        <p className="text-rose-300">Failed to load feature coverage. Are you logged in as admin?</p>
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="min-h-screen bg-white text-slate-900 px-4 sm:px-6 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Layers className="w-6 h-6 text-cyan-300" />
              <h1
                data-testid="feature-coverage-title"
                className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight"
              ><BrandTitle>Feature Coverage</BrandTitle></h1>
            </div>
            <p className="text-sm text-slate-400 max-w-2xl">
              Real-time scan of every page in <code className="text-cyan-300">frontend/src/pages</code>.
              Surfaces which features are live, which are UI-only, and which are stubs —
              so you can see codebase maturity at a glance.
            </p>
          </div>
          <Button
            data-testid="refresh-coverage-btn"
            onClick={() => { setRefreshing(true); fetchData(); }}
            disabled={refreshing}
            className="h-10 bg-emerald-500 hover:bg-cyan-400 text-slate-950 font-semibold border-0 shadow-md shadow-cyan-500/30"
          >
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Re-scan
          </Button>
        </div>

        {/* Coverage hero — vibrant gradient */}
        <Card
          data-testid="coverage-hero"
          className="mb-6 bg-slate-900 bg-gradient-to-r from-emerald-950/60 via-slate-900 to-cyan-950/40 border border-emerald-500/30 shadow-[0_0_40px_-12px_rgba(52,211,153,0.25)] text-slate-100">
          <CardContent className="p-6 flex items-center gap-6 flex-wrap">
            <div className="w-20 h-20 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center shadow-inner shadow-emerald-500/30">
              <span data-testid="coverage-pct" className="text-3xl font-bold text-emerald-200 tabular-nums">
                {summary.coverage_pct}%
              </span>
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="text-lg font-semibold text-white">Production-ready coverage</div>
              <p className="text-sm text-slate-400 mt-1">
                <span className="text-emerald-300 font-bold">{summary.real}</span> live
                ·{' '}
                <span className="text-amber-300 font-bold">{summary.ui_only}</span> UI-only
                ·{' '}
                <span className="text-rose-300 font-bold">{summary.stub}</span> stubs
                ·{' '}
                <span className="text-slate-400 font-bold">{summary.static}</span> static
                <span className="text-slate-400"> / {summary.total_pages} total</span>
              </p>
              <p className="text-[11px] text-slate-400 mt-2">
                Coverage % excludes marketing/legal/auth pages (correctly static).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Filter row */}
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="coverage-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="coverage-search"
              placeholder="Search pages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-slate-200 placeholder:text-slate-400"
            />
          </div>
          {['all', 'real', 'ui_only', 'stub', 'static'].map((f) => {
            const active = filter === f;
            const meta = f === 'all' ? null : STATUS_META[f];
            return (
              <Button
                key={f}
                data-testid={`filter-${f}`}
                variant="outline"
                size="sm"
                onClick={() => setFilter(f)}
                className={`border ${active
                  ? 'bg-emerald-500/15 text-cyan-200 border-emerald-400/40'
                  : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500 hover:text-slate-200'}`}
              >
                {f === 'all' ? 'All' : meta.label}
                <span className={`ml-2 text-[11px] tabular-nums ${active ? 'text-cyan-300' : 'text-slate-400'}`}>
                  {f === 'all' ? data.pages.length : (summary[f] || 0)}
                </span>
              </Button>
            );
          })}
        </div>

        {/* Table */}
        <Card className="bg-slate-900 text-slate-100 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white">
              Pages ({visiblePages.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {visiblePages.length === 0 ? (
              <p className="p-6 text-sm text-slate-400 text-center">
                No pages match the current filter.
              </p>
            ) : (
              <div className="divide-y divide-slate-800/60">
                {visiblePages.map((p) => {
                  const meta = STATUS_META[p.status];
                  const Icon = meta.icon;
                  return (
                    <div
                      key={p.name}
                      data-testid={`coverage-row-${p.name}`}
                      className="px-6 py-3 flex items-center gap-3 hover:bg-slate-900 transition-colors flex-wrap"
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${
                        meta.color === 'emerald' ? 'text-emerald-300' :
                        meta.color === 'amber'   ? 'text-amber-300' :
                        meta.color === 'rose'    ? 'text-rose-300' :
                        'text-slate-400'
                      }`} />
                      <span className="text-sm text-white font-medium min-w-[180px]">
                        {p.name}
                      </span>
                      <Badge className={`text-[11px] tracking-widest font-semibold border ${COLOR_CLASSES[meta.color]}`}>
                        {meta.label.toUpperCase()}
                      </Badge>
                      <span className="flex-1" />
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-slate-400 tabular-nums">
                          {p.lines} lines
                        </span>
                        {p.has_api_call && (
                          <span className="text-emerald-400 text-[11px] font-semibold">API</span>
                        )}
                        {p.has_mock_data && !p.has_api_call && (
                          <span className="text-amber-400 text-[11px] font-semibold">MOCK</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-[11px] text-slate-400 text-center">
          Last scanned {new Date(data.scanned_at).toLocaleString()} · founder-private · never auto-shared.
        </p>
      </div>
    </div>
  );
};

export default AdminFeatureCoverage;
